import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthStatus } from "../components/hooks/useAuthStatus";
import { isAllowedEmailUser } from "../components/utils/access";
import {
  calculateDVWStats,
  extractSetScoresFromDVWText,
  generateMatch,
} from "../components/utils/DVWFile";
import { matchToVsmJson } from "../components/utils/DVWToVSM";
import { buildDVWMatchText } from "../components/utils/DVWWriter";
import {
  calculatePSVBStats,
  initWithPSVBCompressedBuffer,
} from "../components/utils/PSVBFile";
import { calculateSideoutStats } from "../components/utils/StatsItem";
import { detectClosingScore, generateUUID, unzipBuffer } from "../components/utils/Utils";
import { loadVISAsJson } from "../components/utils/VISFile";
import { visJsonToMatchData } from "../components/utils/VISToDVW";
import { vsmJsonToMatchData } from "../components/utils/VSMFile";
import { useLanguage } from "../context/Language/LanguageContext";
import { storeSession } from "../context/VBLiveAPI/VBLiveAPIAction";

function Input() {
  const navigate = useNavigate();
  const dvRef = useRef();
  const { currentUser } = useAuthStatus();
  const { t } = useLanguage();
  const [dvwFileName, setDvwFileName] = useState(null);
  const [dvwFileData, setDvwFileData] = useState(null);
  const psRef = useRef();
  const [psvbFileName, setPsvbFileName] = useState(null);
  const [psvbFileData, setPsvbFileData] = useState(null);
  const plRef = useRef();
  const [playlistFileName, setPlaylistFileName] = useState(null);
  const [playlistFileData, setPlaylistFileData] = useState(null);
  const visRef = useRef();
  const [visFileName, setVisFileName] = useState(null);
  const vsmRef = useRef();
  const [vsmFileName, setVsmFileName] = useState(null);
  const convertDvwRef = useRef();
  const convertVsmRef = useRef();
  const [converting, setConverting] = useState(false);
  const [shiftJIS, setShiftJIS] = useState(false);
  // Optional override for the standard FIVB set-winning rules (25 points per
  // set, 15 for the deciding set, win by 2) used when computing a DataVolley
  // match's set score on import — for matches that don't follow those rules
  // (shortened/custom-format sets). Empty winScore = standard rule.
  const [customWinScore, setCustomWinScore] = useState("");
  const [requireWinByTwo, setRequireWinByTwo] = useState(true);
  const [importing, setImporting] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [pendingImport, setPendingImport] = useState(null); // { text, file, appType, match, navigateState }
  const [alwaysSave, setAlwaysSave] = useState(false);
  const [modalSaving, setModalSaving] = useState(false);
  const [savingToDb, setSavingToDb] = useState(false);

  // Decodes a file's raw bytes into text, auto-detecting the character
  // encoding so player names with accented or non-Latin characters are not
  // corrupted into "???" / mojibake. Modern DataVolley/VBStats files are often
  // saved as UTF-8, while older ones use a legacy Windows code page.
  const decodeFileBuffer = (buffer) => {
    const bytes = new Uint8Array(buffer);

    // Honour an explicit Shift-JIS selection (Japanese DataVolley files).
    if (shiftJIS) {
      return new TextDecoder("shift-jis").decode(bytes);
    }

    // A UTF-8 byte-order mark means the file is definitely UTF-8.
    if (
      bytes.length >= 3 &&
      bytes[0] === 0xef &&
      bytes[1] === 0xbb &&
      bytes[2] === 0xbf
    ) {
      return new TextDecoder("utf-8").decode(bytes);
    }

    // Try a strict UTF-8 decode: if the bytes form well-formed UTF-8 (i.e. the
    // file really is UTF-8) use it; otherwise fall back to a legacy single-byte
    // code page. windows-1252 is a superset of ISO-8859-1 and matches what most
    // DataVolley files on Windows actually use.
    try {
      return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    } catch (e) {
      return new TextDecoder("windows-1252").decode(bytes);
    }
  };

  // Triggers a browser download of generated text content (used by the
  // DVW<->VSM conversion buttons below — these are standalone file-to-file
  // conversions, not imports, so nothing is parsed into a session/saved to
  // the database).
  const downloadTextFile = (filename, content, mimeType = "text/plain") => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Reads a single file as text, auto-detecting the encoding.
  const readFileText = (file) =>
    new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = (e) => resolve(decodeFileBuffer(e.target.result));
      fileReader.onerror = reject;
      fileReader.readAsArrayBuffer(file);
    });

  // The "Points to close a set" override, shared by the initial
  // preview parse (buildMatch) AND the navigation state handed to
  // Session.jsx — Session.jsx re-parses the raw DVW text from scratch (it
  // doesn't reuse the match object built here), so without this the override
  // only affected the save-to-DB preview and the displayed match score would
  // silently revert to the standard 25/15 rules. When the user hasn't typed a
  // value, auto-detect one from the file's own final set scores (e.g. every
  // set finishing at 18, or any other value consistently below 25) so a
  // custom-capped match still computes the right sets-won score without the
  // user having to notice and fill in the field themselves.
  const resolveWinScoreOptions = (dvwText) => {
    if (customWinScore !== "") {
      const winScore = parseInt(customWinScore, 10);
      return {
        winScore: Number.isFinite(winScore) ? winScore : undefined,
        winByTwo: requireWinByTwo,
        autoDetected: false,
      };
    }
    const detected = detectClosingScore(extractSetScoresFromDVWText(dvwText));
    return {
      winScore: detected ?? undefined,
      winByTwo: requireWinByTwo,
      autoDetected: detected != null,
    };
  };

  // Parses raw file text into a match object ready for display or upload.
  const buildMatch = (text, file, appType) => {
    var mx;
    if (appType === "DataVolley") {
      const opts = resolveWinScoreOptions(text);
      var dm = generateMatch(text, opts);
      dm.filename = file.name;
      dm.buffer = text;
      mx = calculateDVWStats(dm);
      mx.app = "DataVolley";
      mx = calculateSideoutStats(mx, "DataVolley");
      // Persist the chosen rule on the match so it survives a save/reload
      // (see storeSession/fsStoreSession + Session.jsx's re-parse).
      mx.winScore = opts.winScore ?? null;
      mx.winByTwo = opts.winByTwo;
      mx.autoDetectedWinScore = opts.autoDetected ? opts.winScore : null;
    } else {
      var pm = initWithPSVBCompressedBuffer(text);
      pm.filename = file.name;
      pm.buffer = unzipBuffer(text);
      mx = calculatePSVBStats(pm);
      mx.app = "VBStats";
      mx = calculateSideoutStats(mx, "VBStats");
    }
    if (!mx.guid) {
      mx.guid = generateUUID();
    }
    return mx;
  };

  // One aggregate toast (instead of one per file) when a multi-file import
  // auto-detected a custom set-closing score for some of the files.
  const toastAutoDetectedSummary = (items) => {
    const count = items.filter((item) => item.match?.autoDetectedWinScore != null).length;
    if (count > 0) {
      toast.info(
        `${t("Auto-detected a custom set-closing score for")} ${count} ${t("file(s).")}`
      );
    }
  };

  // Executes the save-to-DB + navigate flow used by both the modal and the
  // auto-save path.
  const saveAndOpenSession = async (pending) => {
    const { text, file, appType, match, navigateState } = pending;
    setSavingToDb(true);
    try {
      const ret = await storeSession(match, currentUser);
      if (ret === 0) {
        toast.error(t("Error saving to database"));
      } else {
        localStorage.removeItem("liveMatches");
        toast.success(t("Saved to database"));
      }
    } catch (e) {
      toast.error(t("Error saving to database"));
    } finally {
      setSavingToDb(false);
    }
    navigate("/session", { state: { ...navigateState, initialInDatabase: true } });
  };

  // Handles one or many DataVolley/VBStats files. A single file opens in the
  // session view (existing behaviour); multiple files are parsed and the user
  // is asked whether to upload them all to the database.
  const importMatchFiles = async (fileList, appType) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) {
      return;
    }

    if (appType === "DataVolley") {
      setDvwFileName(
        files.length === 1 ? files[0].name : `${files.length} files`
      );
    } else {
      setPsvbFileName(
        files.length === 1 ? files[0].name : `${files.length} files`
      );
    }

    // Single file: parse and ask user whether to save to DB.
    if (files.length === 1) {
      setImporting(true);
      let text;
      try {
        text = await readFileText(files[0]);
      } catch (err) {
        toast.error(`Failed to read ${files[0].name}`);
        setImporting(false);
        return;
      }
      setImporting(false);
      const navState = appType === "DataVolley"
        ? {
            dvwFileData: text,
            filename: files[0],
            ...resolveWinScoreOptions(text),
          }
        : { psvbFileData: text, filename: files[0] };
      if (appType === "DataVolley") setDvwFileData(text);
      else setPsvbFileData(text);
      if (appType === "DataVolley" && navState.autoDetected) {
        toast.info(
          `${t("Detected a custom set-closing score of")} ${navState.winScore} ${t("points (no value was entered) — applied automatically.")}`
        );
      }

      const autoSavePref = localStorage.getItem("autoSaveImport") === "true";
      if (autoSavePref && currentUser?.email) {
        // Auto-save without prompting.
        const match = buildMatch(text, files[0], appType);
        await saveAndOpenSession({ text, file: files[0], appType, match, navigateState: navState });
        return;
      }

      // Show the modal so the user can decide.
      const match = buildMatch(text, files[0], appType);
      setPendingImport({ text, file: files[0], appType, match, navigateState: navState });
      setAlwaysSave(false);
      setShowSaveModal(true);
      return;
    }

    // Multiple files: parse each into a match.
    setImporting(true);
    const matches = [];
    for (const file of files) {
      try {
        const text = await readFileText(file);
        matches.push({ name: file.name, match: buildMatch(text, file, appType) });
      } catch (err) {
        console.error(err);
        toast.error(`Failed to import ${file.name}`);
      }
    }
    setImporting(false);

    if (matches.length === 0) {
      return;
    }
    toastAutoDetectedSummary(matches);

    const autoSavePref = localStorage.getItem("autoSaveImport") === "true";
    const wantsUpload = autoSavePref ||
      window.confirm(`Imported ${matches.length} file(s).\n\nUpload them to the database now?`);
    if (!wantsUpload) {
      toast.info(`Imported ${matches.length} file(s) (not uploaded).`);
      return;
    }

    setImporting(true);
    let uploaded = 0;
    let failed = 0;
    for (const item of matches) {
      const ret = await storeSession(item.match, currentUser);
      if (ret === 0) {
        failed += 1;
        toast.error(`Failed to upload ${item.name}`);
      } else {
        uploaded += 1;
      }
    }
    setImporting(false);
    if (uploaded > 0) {
      toast.success(
        `Uploaded ${uploaded} match(es)${failed ? `, ${failed} failed` : ""}.`
      );
      localStorage.removeItem("liveMatches");
      navigate("/mainpage", { state: { currentContent: 0 } });
    }
  };

  const handleDVWFile = (event) => importMatchFiles(event.target.files, "DataVolley");

  const handlePSVBFile = (event) => importMatchFiles(event.target.files, "VBStats");

  const handlePlaylistFile = (event) => {
    var url = URL.createObjectURL(event.target.files[0]);
    setPlaylistFileName(event.target.files[0].name);
    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(event.target.files[0]);
    fileReader.onload = (e) => {
      const text = decodeFileBuffer(e.target.result);
      setPlaylistFileData(text);
      const st = {
        playlistFileData: text,
        filename: event.target.files[0].name,
        playlist: null,
        currentUser: currentUser.email,
      };
      navigate("/playlist", { state: st });
    };
  };

  // Handles one or many SportsStreamLab / VolleyStation ".vis" files. A single
  // file opens in the session view; multiple files are parsed and the user is
  // asked whether to upload them all to the database.
  const handleVISFile = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setVisFileName(files.length === 1 ? files[0].name : `${files.length} files`);

    // Single file: parse, then ask the user whether to save to DB (same
    // prompt/auto-save flow as DataVolley/PSVB imports — see importMatchFiles).
    if (files.length === 1) {
      setImporting(true);
      let text;
      try {
        const buffer = await files[0].arrayBuffer();
        const json = loadVISAsJson(buffer, files[0].name);
        const data = visJsonToMatchData(json);
        text = buildDVWMatchText(data);
      } catch (err) {
        console.error("Failed to parse VIS file", err);
        toast.error(`Failed to parse ${files[0].name}: ${err.message}`);
        setImporting(false);
        return;
      }
      setImporting(false);
      const opts = resolveWinScoreOptions(text);
      if (opts.autoDetected) {
        toast.info(
          `${t("Detected a custom set-closing score of")} ${opts.winScore} ${t("points (no value was entered) — applied automatically.")}`
        );
      }
      const navState = { dvwFileData: text, filename: files[0], origin: "VIS", ...opts };
      const match = buildMatch(text, files[0], "DataVolley");
      match.origin = "VIS";

      const autoSavePref = localStorage.getItem("autoSaveImport") === "true";
      if (autoSavePref && currentUser?.email) {
        await saveAndOpenSession({ text, file: files[0], appType: "DataVolley", match, navigateState: navState });
        return;
      }

      setPendingImport({ text, file: files[0], appType: "DataVolley", match, navigateState: navState });
      setAlwaysSave(false);
      setShowSaveModal(true);
      return;
    }

    // Multiple files: parse each into a match then offer to upload.
    setImporting(true);
    const matches = [];
    for (const file of files) {
      try {
        const buffer = await file.arrayBuffer();
        const json = loadVISAsJson(buffer, file.name);
        const data = visJsonToMatchData(json);
        const text = buildDVWMatchText(data);
        matches.push({ name: file.name, match: buildMatch(text, file, "DataVolley") });
      } catch (err) {
        console.error(err);
        toast.error(`Failed to import ${file.name}`);
      }
    }
    setImporting(false);

    if (matches.length === 0) return;
    toastAutoDetectedSummary(matches);

    const autoSavePref = localStorage.getItem("autoSaveImport") === "true";
    const wantsUpload = autoSavePref ||
      window.confirm(`Imported ${matches.length} VIS file(s).\n\nUpload them to the database now?`);
    if (!wantsUpload) {
      toast.info(`Imported ${matches.length} VIS file(s) (not uploaded).`);
      return;
    }

    setImporting(true);
    let uploaded = 0;
    let failed = 0;
    for (const item of matches) {
      const ret = await storeSession(item.match, currentUser);
      if (ret === 0) {
        failed += 1;
        toast.error(`Failed to upload ${item.name}`);
      } else {
        uploaded += 1;
      }
    }
    setImporting(false);
    if (uploaded > 0) {
      toast.success(
        `Uploaded ${uploaded} VIS match(es)${failed ? `, ${failed} failed` : ""}.`
      );
      localStorage.removeItem("liveMatches");
      navigate("/mainpage", { state: { currentContent: 0 } });
    }
  };

  // Handles one or many VolleyStation ".vsm" files (a JSON export that's
  // already shaped very close to DataVolley's own conventions). A single
  // file opens in the session view; multiple files are parsed and the user is
  // asked whether to upload them all to the database. Mirrors handleVISFile.
  const handleVSMFile = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setVsmFileName(files.length === 1 ? files[0].name : `${files.length} files`);

    // Single file: parse, then ask the user whether to save to DB (same
    // prompt/auto-save flow as DataVolley/PSVB imports — see importMatchFiles).
    if (files.length === 1) {
      setImporting(true);
      let dvwText;
      try {
        const text = await readFileText(files[0]);
        const json = JSON.parse(text);
        const data = vsmJsonToMatchData(json);
        dvwText = buildDVWMatchText(data);
      } catch (err) {
        console.error("Failed to parse VSM file", err);
        toast.error(`Failed to parse ${files[0].name}: ${err.message}`);
        setImporting(false);
        return;
      }
      setImporting(false);
      const opts = resolveWinScoreOptions(dvwText);
      if (opts.autoDetected) {
        toast.info(
          `${t("Detected a custom set-closing score of")} ${opts.winScore} ${t("points (no value was entered) — applied automatically.")}`
        );
      }
      const navState = { dvwFileData: dvwText, filename: files[0], origin: "VSM", ...opts };
      const match = buildMatch(dvwText, files[0], "DataVolley");
      match.origin = "VSM";

      const autoSavePref = localStorage.getItem("autoSaveImport") === "true";
      if (autoSavePref && currentUser?.email) {
        await saveAndOpenSession({ text: dvwText, file: files[0], appType: "DataVolley", match, navigateState: navState });
        return;
      }

      setPendingImport({ text: dvwText, file: files[0], appType: "DataVolley", match, navigateState: navState });
      setAlwaysSave(false);
      setShowSaveModal(true);
      return;
    }

    // Multiple files: parse each into a match then offer to upload.
    setImporting(true);
    const matches = [];
    for (const file of files) {
      try {
        const text = await readFileText(file);
        const json = JSON.parse(text);
        const data = vsmJsonToMatchData(json);
        const dvwText = buildDVWMatchText(data);
        matches.push({ name: file.name, match: buildMatch(dvwText, file, "DataVolley") });
      } catch (err) {
        console.error(err);
        toast.error(`Failed to import ${file.name}`);
      }
    }
    setImporting(false);

    if (matches.length === 0) return;
    toastAutoDetectedSummary(matches);

    const autoSavePref = localStorage.getItem("autoSaveImport") === "true";
    const wantsUpload = autoSavePref ||
      window.confirm(`Imported ${matches.length} VSM file(s).\n\nUpload them to the database now?`);
    if (!wantsUpload) {
      toast.info(`Imported ${matches.length} VSM file(s) (not uploaded).`);
      return;
    }

    setImporting(true);
    let uploaded = 0;
    let failed = 0;
    for (const item of matches) {
      const ret = await storeSession(item.match, currentUser);
      if (ret === 0) {
        failed += 1;
        toast.error(`Failed to upload ${item.name}`);
      } else {
        uploaded += 1;
      }
    }
    setImporting(false);
    if (uploaded > 0) {
      toast.success(
        `Uploaded ${uploaded} VSM match(es)${failed ? `, ${failed} failed` : ""}.`
      );
      localStorage.removeItem("liveMatches");
      navigate("/mainpage", { state: { currentContent: 0 } });
    }
  };

  const toggleShiftJIS = () => {
    setShiftJIS((prev) => !prev);
    localStorage.setItem("shiftJIS", !shiftJIS);
  };

  // Standalone file conversion -- parses a real DataVolley ".dvw" file and
  // downloads the equivalent VolleyStation ".vsm" JSON, with no import/save
  // to the database (see DVWToVSM.js for what is/isn't preserved).
  const handleConvertDvwToVsm = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setConverting(true);
    try {
      const buffer = await file.arrayBuffer();
      const text = decodeFileBuffer(buffer);
      const match = calculateDVWStats(generateMatch(text));
      const vsmJson = matchToVsmJson(match);
      const outName = file.name.replace(/\.dvw$/i, "") + ".vsm";
      downloadTextFile(outName, JSON.stringify(vsmJson, null, 2), "application/json");
      toast.success(`${t("Converted")} ${file.name} -> ${outName}`);
    } catch (err) {
      console.error("Failed to convert DVW to VSM", err);
      toast.error(`${t("Failed to convert")} ${file.name}: ${err.message}`);
    }
    setConverting(false);
  };

  // Standalone file conversion -- parses a VolleyStation ".vsm" JSON export
  // and downloads the equivalent DataVolley ".dvw" text, with no import/save
  // to the database.
  const handleConvertVsmToDvw = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setConverting(true);
    try {
      const text = await readFileText(file);
      const json = JSON.parse(text);
      const data = vsmJsonToMatchData(json);
      const dvwText = buildDVWMatchText(data);
      const outName = file.name.replace(/\.vsm$/i, "") + ".dvw";
      downloadTextFile(outName, dvwText, "text/plain");
      toast.success(`${t("Converted")} ${file.name} -> ${outName}`);
    } catch (err) {
      console.error("Failed to convert VSM to DVW", err);
      toast.error(`${t("Failed to convert")} ${file.name}: ${err.message}`);
    }
    setConverting(false);
  };


  useEffect(() => {
    const sj = localStorage.getItem("shiftJIS");
    if (sj) {
      setShiftJIS(sj);
    }
  }, []);

  return (
    <div className="flex min-h-full justify-center px-4 py-12">
      <div className="flex-col">
        {/* <SessionSearch /> */}
        <div className="flex my-4">
          <input
            type="file"
            id="selectedDVWFile"
            ref={dvRef}
            multiple
            style={{ display: "none" }}
            onChange={handleDVWFile}
            onClick={(event) => {
              event.target.value = null;
            }}
          />
          <input
            type="button"
            className="btn-in-form w-60"
            value={t("Load DVW file(s)...")}
            onClick={() => document.getElementById("selectedDVWFile").click()}
          />
          <label className="label ml-4">
            <span className="label-text">
              {dvwFileName === null
                ? t("load one or more DataVolley files")
                : dvwFileName}
            </span>
          </label>
        </div>

        <div className="flex my-4">
          <input
            type="file"
            id="selectedVSMFile"
            ref={vsmRef}
            accept=".vsm"
            multiple
            style={{ display: "none" }}
            onChange={handleVSMFile}
            onClick={(event) => {
              event.target.value = null;
            }}
          />
          <input
            type="button"
            className="btn-in-form w-60"
            value={t("Load VSM file(s)...")}
            onClick={() => document.getElementById("selectedVSMFile").click()}
          />
          <label className="label ml-4">
            <span className="label-text">
              {vsmFileName === null
                ? t("load one or more VolleyStation VSM files")
                : vsmFileName}
            </span>
          </label>
        </div>

        <div className="flex my-4">
          <input
            type="file"
            id="selectedPSVBFile"
            ref={psRef}
            multiple
            style={{ display: "none" }}
            onChange={handlePSVBFile}
            onClick={(event) => {
              event.target.value = null;
            }}
          />
          <input
            type="button"
            className="btn-in-form w-60"
            value={t("Load PSVB file(s)...")}
            onClick={() => document.getElementById("selectedPSVBFile").click()}
          />
          <label className="label ml-4">
            <span className="label-text">
              {psvbFileName === null
                ? t("load one or more VBStats files")
                : psvbFileName}
            </span>
          </label>
        </div>

        <div className="flex my-4">
          <input
            type="file"
            id="selectedVISFile"
            ref={visRef}
            accept=".vis"
            multiple
            style={{ display: "none" }}
            onChange={handleVISFile}
            onClick={(event) => {
              event.target.value = null;
            }}
          />
          <input
            type="button"
            className="btn-in-form w-60"
            value={t("Load VIS file(s)...")}
            onClick={() => document.getElementById("selectedVISFile").click()}
          />
          <label className="label ml-4">
            <span className="label-text">
              {visFileName === null
                ? t("load one or more AVC VIS files")
                : visFileName}
            </span>
          </label>
        </div>

        <div className="flex my-4">
          <input
            type="file"
            id="selectedPlaylistFile"
            ref={plRef}
            style={{ display: "none" }}
            onChange={handlePlaylistFile}
            onClick={(event) => {
              event.target.value = null;
            }}
          />
          <input
            type="button"
            className="btn-in-form w-60"
            value={t("Load playlist file...")}
            onClick={() =>
              document.getElementById("selectedPlaylistFile").click()
            }
          />
          <label className="label ml-4">
            <span className="label-text">
              {playlistFileName === null
                ? t("load a playlist")
                : playlistFileName}
            </span>
          </label>
        </div>

        <div className="divider">{t("Convert DVW")} ⇄ {t("VSM")}</div>

        {isAllowedEmailUser(currentUser) ? (
          <>
            <div className="flex my-4">
              <input
                type="file"
                id="convertDvwFile"
                ref={convertDvwRef}
                accept=".dvw"
                style={{ display: "none" }}
                onChange={handleConvertDvwToVsm}
                onClick={(event) => {
                  event.target.value = null;
                }}
              />
              <input
                type="button"
                className="btn-in-form w-60"
                value={t("Convert DVW to VSM...")}
                onClick={() => document.getElementById("convertDvwFile").click()}
              />
              <label className="label ml-4">
                <span className="label-text">
                  {t("pick a DataVolley .dvw file to download as .vsm")}
                </span>
              </label>
            </div>

            <div className="flex my-4">
              <input
                type="file"
                id="convertVsmFile"
                ref={convertVsmRef}
                accept=".vsm"
                style={{ display: "none" }}
                onChange={handleConvertVsmToDvw}
                onClick={(event) => {
                  event.target.value = null;
                }}
              />
              <input
                type="button"
                className="btn-in-form w-60"
                value={t("Convert VSM to DVW...")}
                onClick={() => document.getElementById("convertVsmFile").click()}
              />
              <label className="label ml-4">
                <span className="label-text">
                  {t("pick a VolleyStation .vsm file to download as .dvw")}
                </span>
              </label>
            </div>
          </>
        ) : (
          <div className="alert alert-info my-4 w-fit">
            <span>{t("Beta testers only — coming soon for everyone else.")}</span>
          </div>
        )}

        <div className="flex gap-2">
          <label className="label label-text ml-4">{t("Use Shift-JIS Encoder")}</label>
          <input
            type="checkbox"
            checked={shiftJIS}
            className="checkbox checkbox-sm mt-2 ml-2 rounded-md"
            onChange={() => toggleShiftJIS()}
          />
        </div>

        <div className="flex items-center gap-2 mt-2">
          <label className="label label-text ml-4">
            {t("Points to close a set")}
          </label>
          <input
            type="number"
            min={1}
            max={99}
            className="input input-sm input-bordered w-20"
            placeholder={t("Standard")}
            value={customWinScore}
            onChange={(e) => setCustomWinScore(e.target.value)}
            title={t(
              "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)",
            )}
          />
          <label className="flex items-center gap-2 ml-2 cursor-pointer">
            <input
              type="checkbox"
              checked={requireWinByTwo}
              className="checkbox checkbox-sm rounded-md"
              onChange={(e) => setRequireWinByTwo(e.target.checked)}
            />
            <span className="label-text">{t("Require 2-point advantage")}</span>
          </label>
        </div>
        {(importing || savingToDb || converting) && (
          <div className="flex items-center gap-2 ml-4 mt-2">
            <span className="loading loading-spinner loading-sm" />
            <span className="label-text">
              {savingToDb
                ? t("Saving to database…")
                : converting
                ? t("Converting…")
                : t("Importing…")}
            </span>
          </div>
        )}
        {/* <SessionResults /> */}
      </div>

      {/* Save-to-database modal for single-file imports */}
      {showSaveModal && pendingImport && (
        <dialog
          className="modal z-[9999]"
          open
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowSaveModal(false);
              navigate("/session", { state: pendingImport.navigateState });
            }
          }}
        >
          <div className="modal-box max-w-sm">
            <h3 className="font-bold text-base mb-1">{t("Save to Database?")}</h3>
            <p className="text-sm text-base-content/70 mb-1">
              {pendingImport.match?.teamA?.Name ?? ""}
              {" vs "}
              {pendingImport.match?.teamB?.Name ?? ""}
            </p>
            <p className="text-xs text-base-content/50 mb-4">
              {pendingImport.match?.TrainingDate
                ? new Date(pendingImport.match.TrainingDate).toLocaleDateString()
                : pendingImport.file?.name}
            </p>
            <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
              <input
                type="checkbox"
                className="checkbox checkbox-sm rounded"
                checked={alwaysSave}
                onChange={(e) => setAlwaysSave(e.target.checked)}
              />
              <span className="text-sm">{t("Always save imports automatically")}</span>
            </label>
            <div className="flex gap-2 justify-end">
              <button
                className="btn btn-sm btn-ghost"
                disabled={modalSaving}
                onClick={() => {
                  setShowSaveModal(false);
                  navigate("/session", { state: pendingImport.navigateState });
                }}
              >
                {t("View Only")}
              </button>
              <button
                className="btn btn-sm btn-primary"
                disabled={modalSaving}
                onClick={async () => {
                  if (alwaysSave) {
                    localStorage.setItem("autoSaveImport", "true");
                  }
                  setModalSaving(true);
                  await saveAndOpenSession(pendingImport);
                  setModalSaving(false);
                  setPendingImport(null);
                }}
              >
                {modalSaving
                  ? <span className="loading loading-spinner loading-xs" />
                  : t("Save to Database")}
              </button>
            </div>
          </div>
          <form
            method="dialog"
            className="modal-backdrop"
            onClick={() => {
              setShowSaveModal(false);
              navigate("/session", { state: pendingImport.navigateState });
            }}
          />
        </dialog>
      )}
    </div>
  );
}

export default Input;
